import {
  STITCH_TYPE_TARGETS,
  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,
  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,
  createExportSteps,
} from '../shared/steps';
import { layers } from './fixture';

// The canvas is always on screen, so it can't tell the tutorial whether a click
// would land. These controls exist only while the app is in the mode the step
// is asking for, which makes them a reliable "you can act now" signal.
const SPLIT_ACTIVE = '[data-tutorial="satin-split-done"]';
const RAIL_SELECTION_ACTIVE = '[data-tutorial="satin-rail-selection-done"]';
const RAIL_PATHS = '[data-tutorial="satin-selectable-path"]';

/**
 * The shapes to cut, as their positions in the black layer's path.
 *
 * The trace puts the ring's outer and inner edges first, then the left eye, the
 * right eye and the smile — check with `node scripts/traceTutorialShapes.js
 * public/assets/embroidery_smiley.png ffe600 000000`. Where to click on each
 * one isn't recorded: the markers are measured off the shape at the two points
 * furthest apart, so they hold up at any zoom, window size or image resolution.
 */
const LEFT_EYE = 2;
const RIGHT_EYE = 3;
const SMILE = 4;

/**
 * Two ways to build a satin column, taught on the Smiley Face.
 *
 * Transcribed from a recorded session on this exact artwork. The design is one
 * black layer holding four separate bands — a ring, two eyes and a smile — and
 * that is the point of the lesson: the ring already has two edges to hand to
 * satin, while the eyes and the smile are solid shapes that have to be cut into
 * edges first. The copy reasons about these particular shapes, so none of it
 * transfers to another design.
 *
 * Steps the learner performs themselves advance on their own click rather than
 * a Next button, so the tutorial keeps pace with the real workflow.
 *
 * The shared prelude — welcome, design picker, processing — is prepended by the
 * provider, so this list starts from the point the Stitch Editor is open.
 */
const SMILEY_STEPS = [
  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,

  {
    id: 'emb-select-layer-2',
    title: 'Pick the Layer to Stitch',
    description: `Click **layer ${layers.features}** — the **black**. It's the ring, the two eyes and the smile, all sewn in one thread.`,
    target: `button[aria-label="Select layer ${layers.features}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-selected',
    actionRequired: true,
  },
  {
    id: 'emb-open-stitch-tool',
    title: 'Open the Stitch Settings',
    description: 'Click **Stitch** to choose how this layer is sewn.',
    target: '[data-tutorial="stitch-tool-stitch"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-stitch-menu',
    actionRequired: true,
  },
  {
    id: 'emb-choose-satin',
    title: 'Choose Satin',
    description: '• **Fill** — solid stitching for large areas\n• **Satin** — smooth, glossy bands for borders and lettering\n• **Running** — a thin single line for fine detail\n\nEvery black shape here is a narrow band, so click **Satin**.',
    target: '[data-tutorial="stitch-type-satin"]',
    // All three are outlined while they're being described; the highlight
    // narrows to Satin on the page that asks for the click.
    pageTargets: [
      { multipleTargets: STITCH_TYPE_TARGETS },
      { target: '[data-tutorial="stitch-type-satin"]' },
    ],
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-satin-chosen',
    actionRequired: true,
  },
  {
    id: 'emb-replace-layer',
    title: 'Replace or Keep Both?',
    description: 'Click **Replace this layer** — we\'re redoing these shapes as satin from scratch, not stitching satin on top of what\'s there.',
    target: '[data-tutorial="stitch-switch-replace-layer"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    // The prompt only appears when there's existing stitching to replace.
    skipIfMissing: 5000,
    waitForAction: 'emb-replace-confirmed',
    actionRequired: true,
  },
  {
    id: 'emb-split-columns',
    title: 'Cut the Solid Shapes Open',
    description: 'Satin runs **between two edges**, and an eye is one solid blob with no edges marked. **Split into Satin Columns** cuts a shape in two down its length, and the two halves become the edges.\n\nClick it to start.',
    target: '[data-tutorial="satin-method-split-into-satin-columns"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-split-mode',
    actionRequired: true,
  },
  {
    id: 'emb-split-shapes',
    title: 'Cut Across Each Shape',
    // One page, no blank line: the markers only appear once the text has been
    // read to the end, and on this step they are the whole instruction.
    description: 'Each shape takes **two clicks — one end, then the other**. Cut the **narrow way** — top to bottom on the eyes, tip to tip on the smile — so the stitches lie across the band. The markers show you where.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    splitShapes: [{ subpath: LEFT_EYE }, { subpath: RIGHT_EYE }, { subpath: SMILE }],
    clickPairs: true,
    // The markers are the instruction here — "the narrow way" only means
    // anything alongside the line drawn across the shape — so they go up with
    // the words rather than after them.
    showPointer: 'immediate',
    readyWhen: SPLIT_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 6,
    waitForAction: 'emb-shapes-split',
    actionRequired: true,
  },
  {
    id: 'emb-split-done',
    title: 'Finish the Cuts',
    description: 'Click **Done** — three columns, three shapes.',
    target: SPLIT_ACTIVE,
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-split-finished',
    actionRequired: true,
  },
  {
    id: 'emb-select-satin-columns',
    title: 'The Ring Is Already Done',
    description: 'The ring is different: it\'s a band, so its **two edges are already drawn** — an outer circle and an inner one. Nothing to cut.\n\nClick **Select Satin Columns** to hand those two paths straight to satin.',
    target: '[data-tutorial="satin-method-select-satin-columns"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rail-mode',
    actionRequired: true,
  },
  {
    id: 'emb-pick-ring-edges',
    title: 'Click the Ring\'s Two Edges',
    description: 'Click the **outside** of the ring, then the **inside**. Satin fills the band between them.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    // The ring's outer contour then its inner one — the first two paths the
    // trace produces. Choosing columns is a path pick rather than a free click,
    // so the markers sit on the strokes themselves and only a hit on one of
    // them counts.
    railPaths: [{ index: 0, at: 0.3 }, { index: 1, at: 0.3 }],
    // Which edge is "the outside" is the marker's job to show, so it appears
    // with the words, as on the cutting step.
    showPointer: 'immediate',
    clickTarget: RAIL_PATHS,
    readyWhen: RAIL_SELECTION_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 2,
    waitForAction: 'emb-edges-picked',
    actionRequired: true,
  },
  {
    id: 'emb-rails-done',
    title: 'Finish the Columns',
    description: 'Click **Done**. Every black shape is satin now.',
    target: RAIL_SELECTION_ACTIVE,
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rails-done',
    actionRequired: true,
  },

  // The panel goes away and the design comes back into view before offsetting is
  // explained, the way the Simple Flower does it: the gap between two colours is
  // the reason for the offset, and it can't be pointed at while only one layer
  // is on screen behind a panel still offering satin options.
  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,

  {
    id: 'emb-offset-intro',
    title: 'Why Layers Need Offsetting',
    description: 'Thread pulls the fabric, so shapes finish **narrower** than they look — leaving bare fabric where two colours meet. Growing the yellow slightly tucks it **under** the black.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    showNext: true,
    highlight: true,
  },
  {
    id: 'emb-select-layer-1',
    title: 'Start With the Face',
    description: `Click **layer ${layers.face}** — the **yellow face**.`,
    target: `button[aria-label="Select layer ${layers.face}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-1',
    actionRequired: true,
  },
  {
    id: 'emb-open-offset',
    title: 'Open Offset',
    description: 'Click **Offset**.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-open',
    actionRequired: true,
  },
  {
    // Setting the amount and applying it is one motion in one open panel, so
    // this can't be two steps: a learner who does the obvious thing would apply
    // before the tutorial asked, get no acknowledgement, then be told to apply
    // again — growing the layer twice. Offsets accumulate, so that quietly
    // damages the design. Advancing on the Apply click accepts either order.
    id: 'emb-offset-amount',
    title: 'Grow It Slightly',
    description: 'Drag the slider to about **0.9 mm** — enough to close the gap, small enough to keep the shape.\n\nThen click **Apply**.',
    target: '[data-tutorial="offset-apply"]',
    // Highlights the slider while the amount is explained, then the button that
    // commits it.
    pageTargets: [
      { target: '[data-tutorial="offset-amount"]' },
      { target: '[data-tutorial="offset-apply"]' },
    ],
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-applied',
    actionRequired: true,
  },

  ...createExportSteps({
    learned: [
      '**Splitting** a solid shape into a satin column',
      '**Selecting** two existing edges when the shape already has them',
      '**Offsets** so neighbouring colours overlap instead of gapping',
      '**Export** at the right format and size',
    ],
  }),
];

export default SMILEY_STEPS;
